import { FormEvent, useMemo, useState } from 'react';
import { Bot, Loader2, MessageCircle, Send, User, X } from 'lucide-react';

import type { SiteContent } from '../../content/types';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  reply: string;
  usedFallback?: boolean;
}

interface FloatingChatbotProps {
  content: SiteContent;
}

export function FloatingChatbot({ content }: FloatingChatbotProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: content.chatbot.welcomeMessage,
    },
  ]);

  const starterQuestions = useMemo(() => content.chatbot.starterQuestions.slice(0, 3), [content.chatbot.starterQuestions]);

  const submitQuestion = async (question: string) => {
    if (!question.trim() || isLoading) {
      return;
    }

    const nextUserMessage: ChatMessage = { role: 'user', content: question.trim() };
    const outgoingHistory = [...messages, nextUserMessage];
    setMessages(outgoingHistory);
    setInputValue('');
    setIsLoading(true);
    setHasError(false);

    try {
      const response = await fetch(content.chatbot.workerEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: question,
          history: outgoingHistory.filter((item) => item.role === 'user'),
        }),
      });

      if (!response.ok) {
        throw new Error('chat_request_failed');
      }

      const payload = (await response.json()) as ChatResponse;
      if (!payload.reply || typeof payload.reply !== 'string') {
        throw new Error('chat_empty_reply');
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: payload.reply }]);
    } catch {
      setHasError(true);
      setMessages((prev) => [...prev, { role: 'assistant', content: content.chatbot.fallbackText }]);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitQuestion(inputValue);
  };

  return (
    <div className="fixed bottom-24 right-4 z-50 sm:bottom-6 sm:right-6">
      {open ? (
        <Card className="w-[min(calc(100vw-2rem),24rem)] border-[color:var(--color-border)] bg-white shadow-calm">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">{content.chatbot.widgetTitle}</CardTitle>
                <p className="text-xs text-[color:var(--color-muted)]">{content.chatbot.widgetSubtitle}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label={content.chatbot.widgetTitle}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
              {messages.map((message, idx) => (
                <div key={`${message.role}-${idx}-${message.content}`} className={cn('flex gap-2', message.role === 'user' ? 'justify-end' : 'justify-start')}>
                  {message.role === 'assistant' ? <Bot className="mt-1 h-4 w-4 shrink-0 text-[color:var(--color-primary)]" /> : null}
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
                      message.role === 'assistant'
                        ? 'bg-[color:var(--color-surface-alt)] text-[color:var(--color-text)]'
                        : 'bg-[color:var(--color-primary)] text-white',
                    )}
                  >
                    {message.content}
                  </div>
                  {message.role === 'user' ? <User className="mt-1 h-4 w-4 shrink-0 text-[color:var(--color-muted)]" /> : null}
                </div>
              ))}
              {isLoading ? (
                <div className="inline-flex items-center gap-2 rounded-2xl bg-[color:var(--color-surface-alt)] px-3 py-2 text-sm text-[color:var(--color-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : null}
            </div>

            {hasError ? (
              <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-alt)] p-3 text-sm">
                <p className="font-semibold text-[color:var(--color-text)]">{content.chatbot.fallbackTitle}</p>
                <p className="mt-1 text-[color:var(--color-muted)]">{content.chatbot.fallbackText}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild size="sm">
                    <a href={content.chatbot.fallbackPrimaryCta.href}>{content.chatbot.fallbackPrimaryCta.label}</a>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <a href={content.chatbot.fallbackSecondaryCta.href}>{content.chatbot.fallbackSecondaryCta.label}</a>
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {starterQuestions.map((question) => (
                <Button key={question} variant="secondary" size="sm" onClick={() => submitQuestion(question)} disabled={isLoading}>
                  {question}
                </Button>
              ))}
            </div>

            <form onSubmit={onSubmit} className="flex items-center gap-2">
              <Input
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder={content.chatbot.inputPlaceholder}
                aria-label={content.chatbot.inputPlaceholder}
                disabled={isLoading}
              />
              <Button type="submit" size="icon" disabled={isLoading || !inputValue.trim()} aria-label={content.chatbot.sendLabel}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
            <p className="text-xs text-[color:var(--color-muted)]">{content.chatbot.disclaimer}</p>
          </CardContent>
        </Card>
      ) : null}

      <Button size="icon" className="ml-auto h-14 w-14 rounded-full shadow-calm" onClick={() => setOpen((prev) => !prev)} aria-label={content.chatbot.widgetTitle}>
        <MessageCircle className="h-6 w-6" />
      </Button>
    </div>
  );
}
